import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiFileText, FiArrowRight, FiCheckCircle, FiDownload, FiDollarSign, FiCpu, FiMic, FiStopCircle, FiSend, FiCopy } from 'react-icons/fi';
import Modal from '../components/ui/Modal';
import PedidoAcciones, { accionesPedido } from '../components/ui/PedidoAcciones';
import ModalEnviarEmailCliente from '../components/ui/ModalEnviarEmailCliente';
import InputDescripcionConOrtografia from '../components/ui/InputDescripcionConOrtografia';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { puede, puedeElegirIA } from '../utils/permisos';
import { useSession } from '../contexts/SessionContext';
import { featureFlagEnabled } from '../config/modulos';
import { accionesDocumento } from '../utils/accionesDocumento';

const LINEA_VACIA = { productoId: '', descripcion: '', observaciones: '', cantidad: 1, precioUnitario: 0, descuento: 0, iva: 21 };
const HORAS_TRABAJADOR_VACIA = { usuarioId: '', nombre: '', horas: 0 };

function normalizarNumeroCustom(tipoDoc, valor) {
  const texto = String(valor ?? '').trim();
  if (!texto) return '';
  const prefijo = tipoDoc === 'facturas' ? 'FAC' : 'PRE';
  const limpio = texto.toUpperCase().replace(/\s+/g, '-');
  const sinPrefijo = limpio.startsWith(`${prefijo}-`) ? limpio.slice(prefijo.length + 1) : limpio.replace(new RegExp(`^${prefijo}-?`, 'i'), '').replace(/^(FAC|PRE)-?/i, '');
  return `${prefijo}-${sinPrefijo.replace(/^-+|-+$/g, '')}`;
}

function calcTotales(lineas) {
  let base = 0, iva = 0;
  for (const l of lineas) {
    const sub = (Number(l.cantidad) || 0) * (Number(l.precioUnitario) || 0) * (1 - (Number(l.descuento) || 0) / 100);
    base += sub;
    iva += sub * (Number(l.iva) || 0) / 100;
  }
  return { base: Math.round(base * 100) / 100, iva: Math.round(iva * 100) / 100, total: Math.round((base + iva) * 100) / 100 };
}

function calcularSimulacionBeneficio({ horasPresupuestadas, horasTrabajadores = [], costeHora = 0, tarifaHora = 0 }) {
  const horasTotalesTrabajadas = horasTrabajadores.reduce((sum, item) => sum + (Number(item.horas) || 0), 0);
  const horasPres = Number(horasPresupuestadas) || 0;
  const costeHoraNum = Number(costeHora) || 0;
  const tarifaHoraNum = Number(tarifaHora) || 0;
  const ingresosEstimados = horasPres * tarifaHoraNum;
  const costeManoObra = horasTotalesTrabajadas * costeHoraNum;
  const beneficio = ingresosEstimados - costeManoObra;
  const margen = ingresosEstimados > 0 ? (beneficio / ingresosEstimados) * 100 : 0;

  return {
    horasTotalesTrabajadas,
    ingresosEstimados,
    costeManoObra,
    beneficio,
    margen,
  };
}

// --- Presupuesto por voz (MVP): validación de audio en cliente ---------------
// Refleja exactamente la allowlist y el límite de tamaño del backend
// (/presupuestos-ia/transcribir) para dar feedback inmediato sin red.
const MAX_AUDIO_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_AUDIO_MB = 10;
const MIME_AUDIO_PERMITIDOS = ['audio/webm', 'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/mp3', 'audio/mpeg', 'audio/ogg', 'audio/m4a', 'audio/x-m4a', 'audio/mp4'];
const MIME_POR_EXTENSION = { webm: 'audio/webm', wav: 'audio/wav', mp3: 'audio/mpeg', mpeg: 'audio/mpeg', ogg: 'audio/ogg', m4a: 'audio/m4a', mp4: 'audio/mp4' };

function validarArchivoAudio(file, nombre) {
  if (!file) return { ok: false, error: 'Graba o selecciona un audio primero' };
  if (file.size > MAX_AUDIO_BYTES) {
    return { ok: false, error: `El audio supera el máximo permitido (${MAX_AUDIO_MB} MB)` };
  }
  let mimeType = (file.type || '').split(';')[0].trim().toLowerCase();
  if (!MIME_AUDIO_PERMITIDOS.includes(mimeType)) {
    const ext = ((nombre || '').split('.').pop() || '').toLowerCase();
    mimeType = MIME_POR_EXTENSION[ext] || mimeType;
  }
  if (!MIME_AUDIO_PERMITIDOS.includes(mimeType)) {
    return { ok: false, error: 'Formato de audio no soportado. Usa webm, wav, mp3, mpeg, ogg o m4a' };
  }
  return { ok: true, mimeType };
}

// Editor de líneas reutilizable
function LineasEditor({ lineas, setLineas, productos, onAddByVoice }) {
  const upd = (i, campo, val) => setLineas((ls) => ls.map((l, idx) => (idx === i ? { ...l, [campo]: val } : l)));
  const elegirProducto = (i, id) => {
    const p = productos.find((x) => x._id === id);
    setLineas((ls) => ls.map((l, idx) => (idx === i ? {
      ...l, productoId: id,
      descripcion: p ? p.nombre : l.descripcion,
      precioUnitario: p ? p.precio : l.precioUnitario,
      iva: p ? p.iva : l.iva,
    } : l)));
  };
  const t = calcTotales(lineas);

  const addLinea = () => setLineas((ls) => [...ls, { ...LINEA_VACIA }]);

  return (
    <div className="span-2 lineas-editor" style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <strong style={{ display: 'block', fontSize: '1rem' }}>Líneas del documento</strong>
          <span style={{ color: 'var(--text-soft)', fontSize: '0.82rem' }}>Añade cada concepto con muy pocos datos.</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={addLinea}><FiPlus /> Añadir línea</button>
          {onAddByVoice && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddByVoice();
              }}
            >
              <FiMic /> Añadir por voz
            </button>
          )}
        </div>
      </div>

      {lineas.length === 0 ? (
        <div className="empty" style={{ padding: 18, borderRadius: 12, border: '1px dashed var(--border, #dbe3ef)', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
          <div>
            <strong style={{ display: 'block', marginBottom: 4 }}>Empieza por la primera línea</strong>
            <span style={{ color: 'var(--text-soft)' }}>Añade el producto, servicio o concepto y luego guarda el documento.</span>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {lineas.map((l, i) => (
            <div key={i} style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 16, boxShadow: '0 6px 18px rgba(15, 23, 42, 0.05)', padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <strong style={{ fontSize: '0.95rem' }}>Línea {i + 1}</strong>
                <button type="button" className="icon-btn danger" aria-label="Eliminar línea" onClick={() => setLineas((ls) => ls.filter((_, idx) => idx !== i))}><FiTrash2 /></button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <label className="field" style={{ margin: 0 }}>
                  <span>Producto</span>
                  <select value={l.productoId} onChange={(e) => elegirProducto(i, e.target.value)} aria-label="Producto">
                    <option value="">Selecciona</option>
                    {productos.map((p) => <option key={p._id} value={p._id}>{p.nombre}</option>)}
                  </select>
                </label>

                <label className="field" style={{ margin: 0, gridColumn: 'span 2' }}>
                  <span>Descripción</span>
                  <InputDescripcionConOrtografia value={l.descripcion} onChange={(val) => upd(i, 'descripcion', val)} placeholder="Ej: Mantenimiento general" />
                </label>

                <label className="field" style={{ margin: 0, gridColumn: 'span 2' }}>
                  <span>Observaciones</span>
                  <input value={l.observaciones || ''} onChange={(e) => upd(i, 'observaciones', e.target.value)} placeholder="Opcional" />
                </label>

                <label className="field" style={{ margin: 0 }}>
                  <span>Cantidad</span>
                  <input type="number" min="0" step="any" value={l.cantidad} onChange={(e) => upd(i, 'cantidad', e.target.value)} aria-label="Cantidad" />
                </label>

                <label className="field" style={{ margin: 0 }}>
                  <span>Precio</span>
                  <input type="number" step="0.01" value={l.precioUnitario} onChange={(e) => upd(i, 'precioUnitario', e.target.value)} aria-label="Precio unitario" />
                </label>

                <label className="field" style={{ margin: 0 }}>
                  <span>Dto %</span>
                  <input type="number" value={l.descuento} onChange={(e) => upd(i, 'descuento', e.target.value)} aria-label="Descuento en porcentaje" />
                </label>

                <label className="field" style={{ margin: 0 }}>
                  <span>IVA %</span>
                  <input type="number" value={l.iva} onChange={(e) => upd(i, 'iva', e.target.value)} aria-label="IVA en porcentaje" />
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="totales" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end', background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)', border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 12, padding: '10px 12px' }}>
        <span>Base: <strong>{t.base.toFixed(2)} €</strong></span>
        <span>IVA: <strong>{t.iva.toFixed(2)} €</strong></span>
        <span>Total: <strong>{t.total.toFixed(2)} €</strong></span>
      </div>
    </div>
  );
}

export default function Presupuestos({ pedidoInicial, onCerrarPedido, onPedidoGuardado, presupuestoInicial, onCerrarPresupuesto, onPresupuestoGuardado, accionDocumento, onCerrarAccion, onDocumentoActualizado, onDocumentoEliminado }) {
  const { usuario, empresa } = useAuth();
  const { abrirSesion } = useSession();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('presupuestos');
  const [presupuestos, setPresupuestos] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modal, setModal] = useState(false);
  const [filtroAutogenerado, setFiltroAutogenerado] = useState('todos');
  const [tipoDoc, setTipoDoc] = useState('presupuestos');
  const [editando, setEditando] = useState(null);
  const [facturaObservaciones, setFacturaObservaciones] = useState(null);
  const [observacionesFactura, setObservacionesFactura] = useState('');
  const [guardandoObservaciones, setGuardandoObservaciones] = useState(false);
  const [cabecera, setCabecera] = useState({ clienteId: '', notas: '', numeroCustom: '' });
  const [enviarAVerifactu, setEnviarAVerifactu] = useState(false);
  const [lineas, setLineas] = useState([]);
  const [horasTrabajadores, setHorasTrabajadores] = useState([]);
  const [horasPresupuestadas, setHorasPresupuestadas] = useState(0);
  const [costeHora, setCosteHora] = useState(0);
  const [tarifaHora, setTarifaHora] = useState(0);
  const [simuladorAbierto, setSimuladorAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [clonando, setClonando] = useState(false);
  const clonandoRef = useRef(false);
  const [convirtiendo, setConvirtiendo] = useState(false);
  const convirtiendoRef = useRef(false);
  const [plantillas, setPlantillas] = useState([]);
  const [iaTexto, setIaTexto] = useState('');
  const [iaGenerando, setIaGenerando] = useState(false);

  // --- Presupuesto por voz (MVP) ---
  const [modalVoz, setModalVoz] = useState(false);
  const [vozGrabando, setVozGrabando] = useState(false);
  const [vozArchivo, setVozArchivo] = useState(null);
  const [vozNombreArchivo, setVozNombreArchivo] = useState('');
  const [vozAudioURL, setVozAudioURL] = useState(null);
  const [vozCargando, setVozCargando] = useState(false);
  const [vozMensaje, setVozMensaje] = useState('');
  const [vozError, setVozError] = useState(null);
  const [vozTexto, setVozTexto] = useState('');
  const [vozLineasEditadas, setVozLineasEditadas] = useState([]);
  const [vozLineasAcumuladas, setVozLineasAcumuladas] = useState([]);
  const [vozAvisos, setVozAvisos] = useState([]);
  const [iaProveedorTexto, setIaProveedorTexto] = useState('groq');
  const [vozProveedorIA, setVozProveedorIA] = useState('groq');
  const [usarIaLineas, setUsarIaLineas] = useState(false);
  const [transcripcionVozGuardada, setTranscripcionVozGuardada] = useState(null);
  const [modalStockForzado, setModalStockForzado] = useState(false);
  const [stockInsuficienteDetalle, setStockInsuficienteDetalle] = useState([]);
  const [comentarioStock, setComentarioStock] = useState('');
  const [payloadPendiente, setPayloadPendiente] = useState(null);
  const [notificacion, setNotificacion] = useState(null);
  const [modalEmailDoc, setModalEmailDoc] = useState(null);
  const [modalGuardado, setModalGuardado] = useState(false);
  const [documentoGuardado, setDocumentoGuardado] = useState(null);
  const [generandoFirma, setGenerandoFirma] = useState(false);
  const [documentosFirmados, setDocumentosFirmados] = useState([]);
  const [pasoActual, setPasoActual] = useState(0);
  const accionInicialRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const vozSoportaGrabacion = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof window !== 'undefined' && !!window.MediaRecorder;

  const puedeVer = puede(usuario, 'presupuestos', 'ver');
  const puedeCrear = puede(usuario, 'presupuestos', 'crear');
  const puedeEditar = puede(usuario, 'presupuestos', 'editar');
  const presupuestoEditable = (doc) => doc && accionesDocumento(usuario, empresa, 'presupuestos', doc).editar;
  const puedeEliminar = puede(usuario, 'presupuestos', 'eliminar');
  // Temporalmente oculto por petición del negocio.
  const puedeAccederSimuladorBeneficio = false;
  const puedeEnviarEmail = usuario?.rol === 'superadmin' || usuario?.rol === 'admin' || puede(usuario, 'clientes', 'enviar_email') || puedeEditar || puedeCrear;
  const puedeElegirProveedorIA = puedeElegirIA(usuario);
  const firmaDigitalHabilitada = featureFlagEnabled(empresa, 'firma_digital');

  useEffect(() => {
    if (!editando || !tipoDoc || tipoDoc === 'pedidos') {
      setDocumentosFirmados([]);
      return;
    }

    const cargarDocumentosFirmados = async () => {
      try {
        const { data } = await api.get('/firma/documentos', {
          params: {
            documentoId: editando._id,
            tipo: tipoDoc === 'facturas' ? 'factura' : 'presupuesto',
          },
        });
        setDocumentosFirmados(Array.isArray(data) ? data : []);
      } catch {
        setDocumentosFirmados([]);
      }
    };

    cargarDocumentosFirmados();
  }, [editando, tipoDoc]);

  const facturaEnviada = (id) => facturas.find((factura) => factura._id === id)?.verifactu?.enviado === true;

  const prepararEnvioFirma = async (doc, tipo = tipoDoc) => {
    if (!doc || !doc._id) return;
    if (!accionesDocumento(usuario, empresa, tipo, doc).enviar) return;
    if (tipo === 'facturas' && (doc.verifactu?.enviado === true || facturaEnviada(doc._id))) return;
    setGenerandoFirma(true);
    const numeroVisibleDoc = numeroVisible(doc);
    const endpoint = tipo === 'facturas'
      ? `/facturas/${doc._id}/generar-link-firma`
      : `/presupuestos/${doc._id}/generar-link-firma`;

    try {
      const { data } = await api.post(endpoint, { confirmarInvalidacion: false });
      const urlFirma = data.urlFirma;
      const clienteObj = data.cliente || clientes.find((c) => String(c._id) === String(doc.clienteId?._id || doc.clienteId)) || { nombre: 'Cliente', email: '' };

      const esFacturaDoc = tipo === 'facturas';
      const nombreDoc = esFacturaDoc ? 'la factura' : 'el presupuesto';
      const tituloDoc = esFacturaDoc ? 'Factura' : 'Presupuesto';
      const numeroVisibleDoc = numeroVisible(doc);

      const asunto = `[${tituloDoc} N° ${numeroVisibleDoc}] - Firma Digital Requerida`;
      const cuerpoTexto =
        `Estimado/a ${clienteObj.nombre || 'cliente'},\n\n` +
        `Le adjuntamos la información correspondiente a ${nombreDoc} N° ${numeroVisibleDoc} por un importe total de ${Number(doc.total || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}.\n\n` +
        `Para revisar los detalles y proceder a la FIRMA DIGITAL y aceptación formal de este documento con pleno valor legal, acceda a su enlace exclusivo y de un solo uso:\n\n` +
        `${urlFirma}\n\n` +
        `Importante: Una vez firmado digitalmente por usted, este enlace de un solo uso caducará automáticamente y quedará registrada su conformidad.\n\n` +
        `Un saludo.`;

      setModalEmailDoc({
        cliente: clienteObj,
        asunto,
        cuerpoTexto,
        tipo: esFacturaDoc ? 'factura' : 'presupuesto',
        documentoId: doc._id,
        documentoTipo: esFacturaDoc ? 'factura' : 'presupuesto',
        numeroDocumento: numeroVisibleDoc,
        numeroCustom: doc.numeroCustom || '',
      });
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.requiereConfirmacion) {
        const confirmar = window.confirm(err.response?.data?.error || 'Ya existe un enlace de firma pendiente para este documento. ¿Deseas invalidar el anterior y continuar con la nueva versión?');
        if (!confirmar) {
          return;
        }
        if (tipo === 'facturas' && (doc.verifactu?.enviado === true || facturaEnviada(doc._id))) return;
        try {
          const { data } = await api.post(endpoint, { confirmarInvalidacion: true });
          const urlFirma = data.urlFirma;
          const clienteObj = data.cliente || clientes.find((c) => String(c._id) === String(doc.clienteId?._id || doc.clienteId)) || { nombre: 'Cliente', email: '' };

          const esFacturaDoc = tipo === 'facturas';
          const nombreDoc = esFacturaDoc ? 'la factura' : 'el presupuesto';
          const tituloDoc = esFacturaDoc ? 'Factura' : 'Presupuesto';

          const asunto = `[${tituloDoc} N° ${numeroVisibleDoc}] - Firma Digital Requerida`;
          const cuerpoTexto =
            `Estimado/a ${clienteObj.nombre || 'cliente'},\n\n` +
            `Le adjuntamos la información correspondiente a ${nombreDoc} N° ${numeroVisibleDoc} por un importe total de ${Number(doc.total || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}.\n\n` +
            `Para revisar los detalles y proceder a la FIRMA DIGITAL y aceptación formal de este documento con pleno valor legal, acceda a su enlace exclusivo y de un solo uso:\n\n` +
            `${urlFirma}\n\n` +
            `Importante: Una vez firmado digitalmente por usted, este enlace de un solo uso caducará automáticamente y quedará registrada su conformidad.\n\n` +
            `Un saludo.`;

          setModalEmailDoc({
            cliente: clienteObj,
            asunto,
            cuerpoTexto,
            tipo: esFacturaDoc ? 'factura' : 'presupuesto',
            documentoId: doc._id,
            documentoTipo: esFacturaDoc ? 'factura' : 'presupuesto',
            numeroDocumento: numeroVisibleDoc,
            numeroCustom: doc.numeroCustom || '',
          });
          return;
        } catch (confirmErr) {
          alert(confirmErr.response?.data?.error || 'No se pudo continuar con la nueva versión del documento.');
          return;
        }
      }
      alert(err.response?.data?.error || 'No se pudo generar el enlace de firma digital.');
    } finally {
      setGenerandoFirma(false);
    }
  };

  const mostrarNotificacion = useCallback((tipo, texto, duracion = 3200) => {
    setNotificacion({ tipo, texto });
    if (duracion > 0) {
      window.setTimeout(() => {
        setNotificacion((actual) => (actual?.texto === texto ? null : actual));
      }, duracion);
    }
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [pr, pe, fa] = await Promise.all([api.get('/presupuestos'), api.get('/pedidos'), api.get('/facturas')]);
      setPresupuestos(pr.data); setPedidos(pe.data); setFacturas(fa.data);
      setEditando((doc) => pe.data.find((pedido) => pedido._id === doc?._id) || doc);
    } catch { /* noop */ } finally { setCargando(false); }
  }, []);

  useEffect(() => {
    cargar();
    api.get('/clientes').then(({ data }) => setClientes(data)).catch(() => {});
    api.get('/almacen').then(({ data }) => setProductos(data)).catch(() => {});
    api.get('/plantillas-presupuesto').then(({ data }) => setPlantillas(Array.isArray(data) ? data : [])).catch(() => {});
  }, [cargar]);

  const avanzarPaso = () => {
    setPasoActual((p) => {
      if (p === 0 && !cabecera.clienteId) {
        mostrarNotificacion('warning', 'Primero elige un cliente.');
        return p;
      }
      return Math.min(p + 1, 3);
    });
  };

  const manejarTeclaFormulario = (event) => {
    if (event.key === 'Enter' && event.target && event.target.tagName !== 'TEXTAREA') {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const resetFormulario = () => {
    setEditando(null);
    const obsDefecto = (tipoDoc === 'facturas' || tipoDoc === 'factura')
      ? (empresa?.observacionDefectoFactura || empresa?.observacionDefecto || '')
      : (empresa?.observacionDefectoPresupuesto || empresa?.observacionDefecto || '');
    setCabecera({ clienteId: '', notas: obsDefecto, numeroCustom: '' });
    setLineas([]);
    setHorasTrabajadores([]);
    setHorasPresupuestadas(0);
    setCosteHora(0);
    setTarifaHora(0);
    setEnviarAVerifactu(false);
    setIaTexto('');
    setUsarIaLineas(false);
    setTranscripcionVozGuardada(null);
    setPayloadPendiente(null);
    setComentarioStock('');
    setPasoActual(0);
    setModal(false);
  };

  const obsDefectoActual = (tipoDoc === 'facturas' || tipoDoc === 'factura')
    ? (empresa?.observacionDefectoFactura || empresa?.observacionDefecto || '')
    : (empresa?.observacionDefectoPresupuesto || empresa?.observacionDefecto || '');

  const tieneBorradorSinGuardar = !editando && (
    cabecera.clienteId ||
    String(cabecera.notas || '').trim() !== String(obsDefectoActual || '').trim() ||
    String(cabecera.numeroCustom || '').trim() ||
    lineas.some((l) => (
      String(l.descripcion || '').trim() ||
      String(l.observaciones || '').trim() ||
      String(l.productoId || '').trim() ||
      Number(l.cantidad) !== 1 ||
      Number(l.precioUnitario) > 0 ||
      Number(l.descuento) > 0 ||
      Number(l.iva) !== 21
    ))
  );

  const cerrarFormulario = () => {
    if (onCerrarPresupuesto) { onCerrarPresupuesto(); return; }
    if (onCerrarPedido) { onCerrarPedido(); return; }
    if (tieneBorradorSinGuardar && !window.confirm('Tienes un presupuesto en borrador sin guardar. ¿Seguro que quieres salir? Se perderán los datos.')) {
      return;
    }
    resetFormulario();
  };

  const abrirNuevo = (tipo, clienteId = '') => {
    setTipoDoc(tipo); setEditando(null);
    const obsDefecto = (tipo === 'facturas' || tipo === 'factura')
      ? (empresa?.observacionDefectoFactura || empresa?.observacionDefecto || '')
      : (empresa?.observacionDefectoPresupuesto || empresa?.observacionDefecto || '');
    setCabecera({ clienteId, notas: obsDefecto, numeroCustom: '' }); setLineas([]);
    setHorasTrabajadores([]);
    setHorasPresupuestadas(0);
    setCosteHora(0);
    setTarifaHora(0);
    setSimuladorAbierto(false);
    setEnviarAVerifactu(false);
    setIaTexto(''); setUsarIaLineas(false);
    setPasoActual(0);
    setModal(true);
  };

  useEffect(() => {
    if (accionInicialRef.current || cargando || !puedeCrear) return;
    const accionInicial = searchParams.get('accion');
    const clienteIdInicial = searchParams.get('clienteId') || '';

    if (clienteIdInicial && !accionInicial) {
      accionInicialRef.current = true;
      abrirNuevo('presupuestos', clienteIdInicial);
      return;
    }
    if (!accionInicial) return;

    accionInicialRef.current = true;
    setCabecera((actual) => ({ ...actual, clienteId: clienteIdInicial }));
    if (accionInicial === 'voz') setModalVoz(true);
    else abrirNuevo('presupuestos', clienteIdInicial);
  }, [cargando, puedeCrear, searchParams]);

  const abrirEditar = async (tipo, doc) => {
    if (tipo === 'facturas') return;
    if (tipo === 'presupuestos' && !presupuestoEditable(doc)) return;
    if (tipo === 'pedidos' && !accionesPedido(usuario, doc).editar) return;
    setTipoDoc(tipo); setEditando(doc);
    setCabecera({
      clienteId: doc.clienteId?._id || doc.clienteId || '',
      notas: doc.observaciones ?? doc.notas ?? '',
      numeroCustom: doc.numeroCustom || '',
    });
    setLineas(doc.lineas?.length ? doc.lineas.map((l) => ({ ...LINEA_VACIA, ...l, productoId: l.productoId || '' })) : []);
    setHorasTrabajadores(Array.isArray(doc.horasTrabajadores) ? doc.horasTrabajadores.map((h) => ({ ...HORAS_TRABAJADOR_VACIA, ...h, usuarioId: h.usuarioId || '', nombre: h.nombre || '' })) : []);
    setHorasPresupuestadas(Number(doc.horasPresupuestadas) || 0);
    setCosteHora(Number(doc.costeHora) || 0);
    setTarifaHora(Number(doc.tarifaHora) || 0);
    setSimuladorAbierto(Boolean(Number(doc.horasPresupuestadas) > 0 || (Array.isArray(doc.horasTrabajadores) && doc.horasTrabajadores.some((h) => Number(h.horas) > 0)) || Number(doc.costeHora) > 0 || Number(doc.tarifaHora) > 0));
    setIaTexto(''); setUsarIaLineas(false);
    setPasoActual(0);
    setModal(true);
  };

  useEffect(() => {
    if (pedidoInicial) abrirEditar('pedidos', pedidoInicial);
  }, [pedidoInicial]);

  useEffect(() => {
    if (presupuestoInicial) abrirEditar('presupuestos', presupuestoInicial);
  }, [presupuestoInicial]);

  const abrirObservacionesFactura = (doc) => {
    if (!puedeEditar || doc.verifactu?.enviado || doc.firmaInfo?.firmado) return;
    setFacturaObservaciones(doc);
    setObservacionesFactura(doc.observaciones ?? doc.notas ?? '');
  };

  const guardarObservacionesFactura = async (event) => {
    event.preventDefault();
    const doc = facturas.find((factura) => factura._id === facturaObservaciones?._id) || accionDocumento?.documento;
    if (!doc || !accionesDocumento(usuario, empresa, 'facturas', doc).editar || facturaObservaciones?.verifactu?.enviado || facturaObservaciones?.firmaInfo?.firmado || guardandoObservaciones) return;
    setGuardandoObservaciones(true);
    try {
      await api.put(`/facturas/${doc._id}`, { observaciones: observacionesFactura, notas: observacionesFactura });
      setFacturaObservaciones(null);
      await cargar();
      await onDocumentoActualizado?.();
      mostrarNotificacion('success', 'Observaciones de la factura guardadas');
    } catch (err) {
      mostrarNotificacion('error', err.response?.data?.error || 'No se pudieron guardar las observaciones');
    } finally { setGuardandoObservaciones(false); }
  };

  useEffect(() => {
    if (!accionDocumento) return;
    const { tipo, documento, accion: nombreAccion } = accionDocumento;
    if (nombreAccion === 'editar') abrirObservacionesFactura(documento);
    if (nombreAccion === 'enviar') prepararEnvioFirma(documento, tipo);
  }, [accionDocumento]);

  const cargarPlantilla = (id) => {
    if (!id) return;
    const pl = plantillas.find((p) => p._id === id);
    if (!pl || !pl.lineas?.length) return;
    if (lineas.length > 0 && lineas.some((l) => l.descripcion)) {
      if (!window.confirm('¿Reemplazar las líneas actuales con las de la plantilla?')) return;
    }
    setLineas(pl.lineas.map((l) => ({ ...LINEA_VACIA, descripcion: l.descripcion || '', cantidad: l.cantidad || 1, precioUnitario: l.precioUnitario || 0, iva: l.iva || 21 })));
  };

  const generarConIA = async () => {
    if (!iaTexto.trim()) return;
    setIaGenerando(true);
    try {
      const { data } = await api.post('/presupuestos-ia/generar-lineas', { texto: iaTexto, aiProvider: iaProveedorTexto });
      const nuevas = (data.lineas || []).map((l) => ({ ...LINEA_VACIA, ...l }));
      setLineas((ls) => [...ls, ...nuevas]);
      mostrarNotificacion('success', 'Líneas generadas con IA correctamente');
    } catch (err) {
      if (err.response?.status === 503) {
        mostrarNotificacion('error', 'La función de IA no está disponible en este momento');
      } else {
        mostrarNotificacion('error', err.response?.data?.error || 'Error al generar con IA');
      }
    } finally { setIaGenerando(false); }
  };

  const onGuardar = async (e) => {
    e.preventDefault();
    if (tipoDoc === 'presupuestos' && editando && (!presupuestoEditable(presupuestos.find((doc) => doc._id === editando._id) || editando) || !presupuestoEditable(editando) || guardando)) return;
    if (tipoDoc === 'pedidos' && editando && (!accionesPedido(usuario, editando).editar || guardando)) return;
    if (tipoDoc === 'facturas' && editando && (editando.verifactu?.enviado === true || facturaEnviada(editando._id))) return;
    if (!cabecera.clienteId) { mostrarNotificacion('warning', 'Selecciona un cliente antes de guardar'); return; }
    setGuardando(true);
    try {
      const payload = {
        clienteId: cabecera.clienteId,
        observaciones: cabecera.notas,
        notas: cabecera.notas,
        lineas,
        horasPresupuestadas,
        costeHora,
        tarifaHora,
        horasTrabajadores,
      };
      if (!numeroCustomBloqueado) {
        payload.numeroCustom = normalizarNumeroCustom(tipoDoc, cabecera.numeroCustom);
      }
      if (!editando && tipoDoc === 'facturas') {
        payload.enviarAVerifactu = enviarAVerifactu === true;
      }
      if (!editando && transcripcionVozGuardada) {
        payload.transcripcionVoz = transcripcionVozGuardada;
      }
      let respuesta;
      if (editando) respuesta = await api.put(`/${tipoDoc}/${editando._id}`, payload);
      else respuesta = await api.post(`/${tipoDoc}`, payload);

      if (onPresupuestoGuardado) { await onPresupuestoGuardado(); return; }
      if (onPedidoGuardado) { await onPedidoGuardado(); return; }

      const docGuardadoFinal = respuesta?.data || { _id: editando?._id, clienteId: cabecera.clienteId, tipo: tipoDoc };
      setDocumentoGuardado(docGuardadoFinal);
      setModal(false);
      setModalGuardado(true);
      cargar();
      setTranscripcionVozGuardada(null);
      mostrarNotificacion('success', `${tipoDoc === 'facturas' ? 'Factura' : tipoDoc === 'pedidos' ? 'Pedido' : 'Presupuesto'} guardado correctamente`);
    } catch (err) {
      if (!editando && tipoDoc === 'facturas' && err.response?.status === 409 && Array.isArray(err.response?.data?.detalle)) {
        setStockInsuficienteDetalle(err.response.data.detalle);
        setPayloadPendiente({ clienteId: cabecera.clienteId, observaciones: cabecera.notas, notas: cabecera.notas, lineas, enviarAVerifactu: enviarAVerifactu === true, transcripcionVoz: transcripcionVozGuardada || undefined });
        setComentarioStock('');
        setModalStockForzado(true);
        mostrarNotificacion('warning', 'Hay stock insuficiente para esta factura. Puedes continuar añadiendo un comentario.');
      } else {
        mostrarNotificacion('error', err.response?.data?.error || 'No se pudo guardar el documento');
      }
    } finally { setGuardando(false); }
  };

  const confirmarStockForzado = async () => {
    if (!comentarioStock.trim()) { mostrarNotificacion('warning', 'Debes indicar un comentario para continuar sin stock suficiente'); return; }
    setGuardando(true);
    try {
      await api.post('/facturas', { ...payloadPendiente, forzarStock: true, comentarioStock: comentarioStock.trim() });
      setModalStockForzado(false); setModal(false); cargar();
      setTranscripcionVozGuardada(null); setPayloadPendiente(null); setComentarioStock('');
      mostrarNotificacion('success', 'Factura creada aunque haya stock insuficiente');
    } catch (err) {
      mostrarNotificacion('error', err.response?.data?.error || 'No se pudo generar la factura con stock forzado');
    } finally { setGuardando(false); }
  };

  const accion = async (url, ok) => {
    const [, tipo, id] = url.split('/');
    const doc = facturas.find((item) => item._id === id) || facturaObservaciones;
    if (tipo === 'facturas' && (!doc || !accionesDocumento(usuario, empresa, tipo, doc).verifactu)) return;
    try { await api.post(url); await cargar(); await onDocumentoActualizado?.(); if (ok) mostrarNotificacion('success', ok); }
    catch (err) {
      const d = err.response?.data;
      mostrarNotificacion('error', d?.detalle ? `${d.error}: ${JSON.stringify(d.detalle)}` : (d?.error || 'No se pudo completar la acción'));
    }
  };

  const convertirPresupuesto = async (doc) => {
    if (!puedeEditar || doc.estado === 'convertido' || convirtiendoRef.current) return;
    convirtiendoRef.current = true;
    setConvirtiendo(true);
    try {
      const { data } = await api.post(`/presupuestos/${doc._id}/convertir`);
      await cargar();
      resetFormulario();
      onCerrarPresupuesto?.();
      setTab('pedidos');
      abrirSesion('pedido', data._id, `Pedido #${data.numero}`);
      mostrarNotificacion('success', 'Convertido en pedido');
    } catch (err) {
      const datos = err.response?.data;
      mostrarNotificacion('error', datos?.detalle ? `${datos.error}: ${JSON.stringify(datos.detalle)}` : (datos?.error || 'No se pudo convertir el presupuesto'));
    } finally {
      convirtiendoRef.current = false;
      setConvirtiendo(false);
    }
  };

  const confirmarAccion = async (mensaje, callback) => {
    if (!window.confirm(mensaje)) return;
    await callback();
  };

  const clonarPresupuesto = async (doc) => {
    if (!puedeVer || !puedeCrear || clonandoRef.current) return;
    await confirmarAccion('Se creará un nuevo presupuesto en borrador. El presupuesto original quedará sin cambios. ¿Deseas continuar?', async () => {
      if (clonandoRef.current) return;
      clonandoRef.current = true;
      setClonando(true);
      try {
        const { data } = await api.post(`/presupuestos/${doc._id}/clonar`);
        await cargar();
        if (puedeEditar) await abrirEditar('presupuestos', data);
        mostrarNotificacion('success', `Presupuesto ${numeroVisible(data)} creado como nuevo borrador`);
      } catch (err) {
        mostrarNotificacion('error', err.response?.data?.error || 'No se pudo clonar el presupuesto');
      } finally {
        clonandoRef.current = false;
        setClonando(false);
      }
    });
  };

  const eliminar = async (tipo, id) => {
    if (!puedeEliminar) return;
    if (tipo === 'facturas' && facturaEnviada(id)) return;
    if (!window.confirm('¿Eliminar este documento?')) return;
    if (tipo === 'facturas' && facturaEnviada(id)) return;
    if (!puedeEliminar) return;
    try {
      await api.delete(`/${tipo}/${id}`);
      await cargar();
      if (editando?._id === id) cerrarFormulario();
      if (facturaObservaciones?._id === id) setFacturaObservaciones(null);
      await onDocumentoEliminado?.();
      mostrarNotificacion('success', 'Documento eliminado');
    } catch (err) { mostrarNotificacion('error', err.response?.data?.error || 'No se pudo eliminar'); }
  };

  const marcarPagada = async (id) => {
    const doc = facturas.find((item) => item._id === id) || facturaObservaciones;
    if (!doc || !accionesDocumento(usuario, empresa, 'facturas', doc).pagar) return;
    if (!window.confirm('¿Marcar esta factura como pagada?')) return;
    if (!accionesDocumento(usuario, empresa, 'facturas', doc).pagar) return;
    try { await api.patch(`/facturas/${id}/pago`, { estado: 'pagada', metodoPago: 'manual' }); await cargar(); await onDocumentoActualizado?.(); mostrarNotificacion('success', 'Factura marcada como pagada'); }
    catch { mostrarNotificacion('error', 'No se pudo actualizar el estado de la factura'); }
  };

  const abrirPDF = (id, tipo = 'facturas') => {
    api.get(`/${tipo}/${id}/pdf`, { responseType: 'blob' })
      .then(({ data }) => window.open(URL.createObjectURL(data), '_blank'))
      .catch(() => mostrarNotificacion('error', 'No se pudo generar el PDF'));
  };

  const nombreCli = (c) => (typeof c === 'object' && c ? c.nombre : '—');
  const fmt = (d) => new Date(d).toLocaleDateString('es-ES');
  const numeroVisible = (doc) => String(doc?.numeroCustom || doc?.numero || '').trim() || String(doc?.numero || '');

  const toBase64 = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.readAsDataURL(file);
  });

  // --- Presupuesto por voz (MVP): grabación, transcripción, generación y análisis ---
  const asignarAudioVoz = (file, nombre) => {
    if (!file) return;
    if (vozAudioURL) URL.revokeObjectURL(vozAudioURL);
    setVozArchivo(file);
    setVozNombreArchivo(nombre);
    setVozAudioURL(URL.createObjectURL(file));
    setVozError(null);
    setVozTexto('');
    setVozLineasEditadas([]);
    setVozAvisos([]);
  };

  const onSeleccionarArchivoVoz = (file) => {
    if (!file) return;
    asignarAudioVoz(file, file.name);
  };

  const iniciarGrabacionVoz = async () => {
    setVozError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const candidatos = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg'];
      const mimeType = candidatos.find((t) => window.MediaRecorder.isTypeSupported?.(t));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        asignarAudioVoz(blob, 'grabacion.webm');
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setVozGrabando(true);
    } catch {
      setVozError('No se pudo acceder al micrófono. Revisa los permisos del navegador.');
    }
  };

  const detenerGrabacionVoz = () => {
    mediaRecorderRef.current?.stop();
    setVozGrabando(false);
  };

  const prepararTextoLineaVoz = (texto) => {
    const base = String(texto || '').trim();
    if (!base) return base;
    return `${base}\n\nInstrucción estricta: devuelve solo una línea de producto o servicio. No combines varios conceptos ni varias líneas en una sola respuesta. Si se mencionan varios artículos, elige el concepto principal y genera una sola línea.`;
  };

  const normalizarLineasVozUnicas = (lineas) => {
    const lista = Array.isArray(lineas) ? lineas : [];
    return lista
      .filter((l) => l && (String(l.descripcion || '').trim() || Number(l.precioUnitario) > 0 || Number(l.cantidad) > 0))
      .slice(0, 1)
      .map((l) => ({
        ...l,
        descripcion: String(l.descripcion || '').trim() || 'Línea por voz',
        cantidad: Number(l.cantidad) || 1,
        precioUnitario: Number(l.precioUnitario) || 0,
        descuento: Number(l.descuento) || 0,
        iva: Number(l.iva) || 21,
        productoId: l.productoId || '',
        observaciones: l.observaciones || l.observacion || '',
      }));
  };

  const generarYAnalizarVoz = async (texto) => {
    setVozMensaje('Generando una sola línea…');
    const textoUnico = prepararTextoLineaVoz(texto);
    const { data: dLineas } = await api.post('/presupuestos-ia/generar-desde-voz', { texto: textoUnico, aiProvider: vozProveedorIA });
    const lineasUnicas = normalizarLineasVozUnicas(dLineas.lineas);
    setVozMensaje('Analizando la línea…');
    const { data: dAnalisis } = await api.post('/presupuestos-ia/analizar-faltantes', { lineas: lineasUnicas });
    setVozLineasEditadas(lineasUnicas);
    setVozAvisos(dAnalisis.avisos || []);
  };

  const ejecutarVoz = async () => {
    const validacion = validarArchivoAudio(vozArchivo, vozNombreArchivo);
    if (!validacion.ok) { setVozError(validacion.error); return; }
    setVozError(null);
    setVozCargando(true);
    try {
      setVozMensaje('Transcribiendo audio…');
      const base64 = await toBase64(vozArchivo);
      const { data: dTexto } = await api.post('/presupuestos-ia/transcribir', { audio: base64, mimeType: validacion.mimeType, aiProvider: vozProveedorIA });
      setVozTexto(dTexto.texto);
      await generarYAnalizarVoz(dTexto.texto);
    } catch (err) {
      setVozError(err.response?.data?.error || 'Error al procesar el audio');
    } finally {
      setVozCargando(false);
      setVozMensaje('');
    }
  };

  const regenerarLineasVoz = async () => {
    if (!vozTexto.trim()) { setVozError('La transcripción no puede estar vacía'); return; }
    setVozError(null);
    setVozCargando(true);
    try { await generarYAnalizarVoz(vozTexto); }
    catch (err) { setVozError(err.response?.data?.error || 'Error al generar líneas'); }
    finally { setVozCargando(false); setVozMensaje(''); }
  };

  const updLineaVoz = (i, campo, val) => setVozLineasEditadas((prev) => prev.map((l, idx) => (idx === i ? { ...l, [campo]: val } : l)));

  const resetVozParaNuevaLinea = () => {
    setVozLineasEditadas([]);
    setVozLineasAcumuladas([]);
    setVozAvisos([]);
    setVozTexto('');
    setVozArchivo(null);
    setVozNombreArchivo('');
    if (vozAudioURL) URL.revokeObjectURL(vozAudioURL);
    setVozAudioURL(null);
    setVozError(null);
    setVozMensaje('');
    setVozCargando(false);
  };

  const abrirModalVozDesdeFormulario = () => {
    resetVozParaNuevaLinea();
    setModalVoz(true);
  };

  const cerrarModalVoz = () => {
    if (vozGrabando) detenerGrabacionVoz();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (vozAudioURL) URL.revokeObjectURL(vozAudioURL);
    setModalVoz(false);
    setVozArchivo(null);
    setVozNombreArchivo('');
    setVozAudioURL(null);
    setVozTexto('');
    setVozLineasEditadas([]);
    setVozLineasAcumuladas([]);
    setVozAvisos([]);
    setVozProveedorIA('groq');
    setVozError(null);
    setVozMensaje('');
    setVozCargando(false);
  };

  const usarLineasVoz = () => {
    const convertidas = vozLineasEditadas.map((l) => ({
      productoId: l.productoId || '',
      descripcion: l.descripcion,
      observaciones: l.observaciones || l.observacion || '',
      cantidad: Number(l.cantidad) || 1,
      precioUnitario: Number(l.precioUnitario) || 0,
      descuento: Number(l.descuento) || 0,
      iva: Number(l.iva) || 21,
    }));

    if (!convertidas.length) {
      setVozError('No hay ninguna línea válida para añadir.');
      return;
    }

    setLineas((prev) => [...prev, ...convertidas]);
    if (vozAudioURL) URL.revokeObjectURL(vozAudioURL);
    setVozArchivo(null);
    setVozNombreArchivo('');
    setVozAudioURL(null);
    setVozTexto('');
    setVozLineasEditadas([]);
    setVozLineasAcumuladas((prev) => {
      const siguiente = [...prev, ...convertidas];
      setVozMensaje(`Línea añadida al documento. Ya llevas ${siguiente.length} línea(s) en este ${tab === 'facturas' ? 'presupuesto' : tab === 'pedidos' ? 'pedido' : 'presupuesto'}.`);
      return siguiente;
    });
    setVozAvisos([]);
    setVozError(null);
    setTranscripcionVozGuardada({ texto: vozTexto, proveedorIA: vozProveedorIA });
  };

  const numeroCustomBloqueado = false;
  const simulacionBeneficio = calcularSimulacionBeneficio({ horasPresupuestadas, horasTrabajadores, costeHora, tarifaHora });

  const guardarDatosInternosPresupuesto = useCallback(async ({ silencioso = true } = {}) => {
    if (!editando?._id) return;
    try {
      await api.put(`/presupuestos/${editando._id}`, {
        horasPresupuestadas,
        costeHora,
        tarifaHora,
        horasTrabajadores,
      });
      if (!silencioso) {
        mostrarNotificacion('success', 'Datos internos del presupuesto guardados');
      }
    } catch (err) {
      console.error('ERROR guardar datos internos presupuesto ->', err?.response?.data || err);
    }
  }, [costeHora, editando?._id, horasPresupuestadas, horasTrabajadores, mostrarNotificacion, tarifaHora]);

  useEffect(() => {
    if (!editando?._id || tipoDoc !== 'presupuestos' || !modal || convirtiendo) return;
    const timeout = window.setTimeout(() => {
      guardarDatosInternosPresupuesto({ silencioso: true });
    }, 500);
    return () => window.clearTimeout(timeout);
  }, [costeHora, editando?._id, guardarDatosInternosPresupuesto, horasPresupuestadas, horasTrabajadores, tarifaHora, tipoDoc, modal, convirtiendo]);

  const pedidosFiltrados = filtroAutogenerado === 'todos'
    ? pedidos
    : pedidos.filter((p) => (filtroAutogenerado === 'si' ? p.autogenerado === true : p.autogenerado !== true));

  const TABS = [
    { k: 'presupuestos', label: 'Presupuestos', data: presupuestos },
    { k: 'pedidos', label: 'Pedidos', data: pedidosFiltrados },
    { k: 'facturas', label: 'Facturas', data: facturas },
  ];
  const actual = TABS.find((t) => t.k === tab);
  const tituloDocumento = tab === 'facturas' ? 'factura' : tab === 'pedidos' ? 'pedido' : 'presupuesto';
  const tituloDocumentoCapitalizado = tab === 'facturas' ? 'Factura' : tab === 'pedidos' ? 'Pedido' : 'Presupuesto';

  return (
    <div className="page">
      {!pedidoInicial && !presupuestoInicial && !accionDocumento && <>
      <div className="page-head">
        <div><h1 className="page-title">Facturación</h1><p className="page-subtitle">Presupuestos, pedidos y facturas</p></div>
        <div className="head-actions">
          {puedeCrear && (
            <button className="btn btn-primary" onClick={() => abrirNuevo(tab)}>
              <FiPlus /> Crear nuevo {tituloDocumento}
            </button>
          )}
        </div>
      </div>

      <div className="page-help-card" role="status">
        <div>
          <strong>Flujo recomendado</strong>
          <p>Elige un cliente, añade líneas o usa IA/voz y guarda para convertir el documento en pedido o factura.</p>
        </div>
        <div className="page-summary">
          <span className="page-pill">Presupuestos: {presupuestos.length}</span>
          <span className="page-pill">Pedidos: {pedidos.length}</span>
          <span className="page-pill">Facturas: {facturas.length}</span>
        </div>
      </div>

      {notificacion && (
        <div className={`alert-${notificacion.tipo}`} role="status" style={{ marginBottom: 14 }}>
          {notificacion.texto}
        </div>
      )}

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.k} className={`tab ${tab === t.k ? 'activo' : ''}`} onClick={() => setTab(t.k)}>
            {t.label} <span className="tab-count">{t.data.length}</span>
          </button>
        ))}
      </div>

      {tab === 'pedidos' && (
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <select className="mini-select" value={filtroAutogenerado} onChange={(e) => setFiltroAutogenerado(e.target.value)}>
            <option value="todos">Todos los pedidos</option>
            <option value="si">Solo autogenerados</option>
            <option value="no">Solo manuales</option>
          </select>
        </div>
      )}

      {cargando ? <p className="empty">Cargando…</p> : actual.data.length === 0 ? <p className="empty"><FiFileText /> No hay {actual.label.toLowerCase()}.</p> : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Número</th><th>Cliente</th><th>Fecha</th><th>Total</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {actual.data.map((d) => (
                <tr key={d._id} onDoubleClick={() => {
                  const tipo = tab === 'facturas' ? 'factura' : tab === 'pedidos' ? 'pedido' : 'presupuesto';
                  const label = tab === 'facturas' ? `Factura #${d.numero}` : tab === 'pedidos' ? `Pedido #${d.numero}` : `Presup. #${d.numero}`;
                  abrirSesion(tipo, d._id, label);
                }} style={{ cursor: 'pointer' }}>
                  <td data-label="Número"><strong>{numeroVisible(d)}</strong></td>
                  <td data-label="Cliente">{nombreCli(d.clienteId)}</td>
                  <td data-label="Fecha">{fmt(d.fecha || d.createdAt)}</td>
                  <td data-label="Total">{(d.total || 0).toFixed(2)} €</td>
                  <td data-label="Estado">
                    <span className="badge badge-info">{d.estado}</span>
                    {tab === 'facturas' && (
                      <span className={`badge ${d.verifactu?.enviado ? 'badge-ok' : (d.verifactu?.estado === 'error' ? 'badge-danger' : 'badge-warning')}`} style={{ marginLeft: 6 }}>
                        Verifactu: {d.verifactu?.enviado ? 'enviada' : (d.verifactu?.estado || 'pendiente')}
                      </span>
                    )}
                    {tab === 'pedidos' && d.autogenerado && (
                      <span className="badge badge-warning" style={{ marginLeft: 6 }} title="Este pedido se generó automáticamente al crear una factura directa">
                        Autogenerado
                      </span>
                    )}
                    {Array.isArray(d.historial) && d.historial.some((h) => h.tipo === 'stock_forzado') && (
                      <span
                        className="badge badge-danger"
                        style={{ marginLeft: 6 }}
                        title={d.historial.filter((h) => h.tipo === 'stock_forzado').map((h) => `${h.comentario || 'Sin comentario'}${h.detalle ? ` — ${JSON.stringify(h.detalle)}` : ''}`).join(' | ')}
                      >
                        Sin stock suficiente
                      </span>
                    )}
                    {d.firmaInfo?.firmado && (
                      <span className="badge badge-ok" style={{ marginLeft: 6 }} title={`Firmado el ${new Date(d.firmaInfo.firmadoAt).toLocaleString('es-ES')} por ${d.firmaInfo.firmadoPorNombre}`}>
                        ✍️ Firmado
                      </span>
                    )}
                    {Array.isArray(d.historial) && d.historial.some((h) => h.tipo === 'voz_ia') && (
                      <span className="badge badge-info" style={{ marginLeft: 6 }} title="Líneas generadas a partir de una transcripción de voz con IA">
                        Voz IA
                      </span>
                    )}
                  </td>
                  <td className="acciones">
                    {(tab === 'presupuestos' || (tab === 'facturas' && !d.verifactu?.enviado)) && puedeEnviarEmail && (
                      <button className="icon-btn" onClick={(e) => { e.stopPropagation(); setTipoDoc(tab); prepararEnvioFirma(d, tab); }} title={`Enviar ${tab === 'facturas' ? 'factura' : 'presupuesto'} al cliente`}><FiSend /></button>
                    )}
                    {tab === 'presupuestos' && (
                      <>
                        {puedeVer && puedeCrear && (
                          <button
                            className="icon-btn"
                            title="Clonar presupuesto"
                            aria-label="Clonar presupuesto"
                            disabled={clonando}
                            onClick={(event) => { event.stopPropagation(); clonarPresupuesto(d); }}
                            onDoubleClick={(event) => event.stopPropagation()}
                          ><FiCopy /></button>
                        )}
                        {presupuestoEditable(d) && <button className="icon-btn" onClick={() => abrirEditar('presupuestos', d)} title="Editar"><FiEdit2 /></button>}
                        {puedeEditar && <button className="icon-btn ok" disabled={convirtiendo || d.estado === 'convertido'} onClick={(event) => { event.stopPropagation(); convertirPresupuesto(d); }} onDoubleClick={(event) => event.stopPropagation()} title={d.estado === 'convertido' ? 'Pedido ya convertido' : 'Convertir en pedido'} aria-label={d.estado === 'convertido' ? 'Pedido ya convertido' : 'Convertir en pedido'}><FiArrowRight /></button>}
                      </>
                    )}
                    {tab === 'pedidos' && (
                      <PedidoAcciones pedido={d} compacto onEditar={(doc) => abrirEditar('pedidos', doc)} onActualizado={cargar} onEliminado={() => { if (editando?._id === d._id) resetFormulario(); }} />
                    )}
                    {(tab === 'presupuestos' || tab === 'facturas') && (
                      <button className="icon-btn" onClick={() => abrirPDF(d._id, tab === 'presupuestos' ? 'presupuestos' : 'facturas')} title="Descargar PDF"><FiDownload /></button>
                    )}
                    {tab === 'facturas' && (
                      <>
                        {puedeEditar && !d.verifactu?.enviado && !d.firmaInfo?.firmado && <button className="icon-btn" onClick={() => abrirObservacionesFactura(d)} title="Editar observaciones" aria-label="Editar observaciones"><FiEdit2 /></button>}
                        {puedeEditar && !d.verifactu?.enviado && <button className="icon-btn" onClick={() => confirmarAccion('¿Enviar esta factura a Verifactu?', () => accion(`/facturas/${d._id}/enviar-verifactu`, 'Factura enviada a Verifactu'))} title="Enviar a Verifactu"><FiSend /></button>}
                        {puedeEditar && !d.verifactu?.enviado && d.estado !== 'pagada' && <button className="icon-btn ok" onClick={() => marcarPagada(d._id)} title="Marcar pagada"><FiCheckCircle /></button>}
                      </>
                    )}
                    {tab !== 'pedidos' && puedeEliminar && (tab !== 'facturas' || !d.verifactu?.enviado) && <button className="icon-btn danger" onClick={() => eliminar(tab, d._id)} title="Eliminar"><FiTrash2 /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>}

      <Modal abierto={modalVoz} onCerrar={cerrarModalVoz} titulo={tab === 'facturas' ? 'Factura por voz' : tab === 'pedidos' ? 'Pedido por voz' : 'Presupuesto por voz'} ancho={720} zIndex={110}>
        {vozLineasEditadas.length === 0 && vozLineasAcumuladas.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="alert-info">
              <strong>Qué debes decir:</strong><br />
              {tab === 'facturas'
                ? 'Graba solo una línea por vez. Describe un único concepto de factura con cantidad, precio y IVA. Cuando la línea esté bien, pulsa “Añadir esta línea”. Ejemplo: “Cambio de aceite, 1 unidad, 35 euros, IVA 21 por ciento.”'
                : tab === 'pedidos'
                  ? 'Graba solo una línea por vez. Describe un único pedido con cantidad, precio y entrega. Cuando la línea esté bien, pulsa “Añadir esta línea”. Ejemplo: “Dos unidades de kit básico, 120 euros cada una, IVA 21.”'
                  : 'Graba solo una línea por vez. Describe un único producto o servicio con cantidad, precio y IVA. Cuando la línea esté bien, pulsa “Añadir esta línea”. Ejemplo: “Cambio de bomba, 1 unidad, 180 euros, IVA 21 por ciento.”'}
            </div>

            {puedeElegirProveedorIA && (
              <label className="field">
                <span>Proveedor de IA</span>
                <select value={vozProveedorIA} onChange={(e) => setVozProveedorIA(e.target.value)}>
                  <option value="local">Local</option>
                  <option value="groq">Groq</option>
                </select>
              </label>
            )}

            {vozSoportaGrabacion && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {!vozGrabando ? (
                  <button type="button" className="btn" onClick={iniciarGrabacionVoz} disabled={vozCargando}><FiMic /> Grabar audio</button>
                ) : (
                  <button type="button" className="btn btn-primary" onClick={detenerGrabacionVoz}><FiStopCircle /> Detener grabación</button>
                )}
                {vozGrabando && <span className="badge badge-danger" aria-live="polite" role="status">● Grabando…</span>}
              </div>
            )}

            <label style={{ display: 'block', border: '2px dashed var(--border)', borderRadius: 12, padding: 24, textAlign: 'center', cursor: vozGrabando ? 'not-allowed' : 'pointer', background: '#f8fafc' }}>
              <span style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: 'var(--text-soft)', fontSize: 13 }}>
                {vozSoportaGrabacion ? 'O sube un archivo de audio' : 'Sube un archivo de audio (tu navegador no permite grabar aquí)'}
              </span>
              <input type="file" accept="audio/*" style={{ display: 'none' }} disabled={vozGrabando} onChange={(e) => onSeleccionarArchivoVoz(e.target.files[0])} />
              <span style={{ color: 'var(--text-soft)' }}>{vozNombreArchivo ? `Archivo seleccionado: ${vozNombreArchivo}` : 'Haz clic aquí (webm, wav, mp3, ogg o m4a, máx. 10 MB)'}</span>
            </label>

            {vozAudioURL && (
              <audio controls src={vozAudioURL} style={{ width: '100%' }}>Tu navegador no soporta la reproducción de audio.</audio>
            )}

            {vozTexto && (
              <label className="field"><span>Transcripción (edítala si es necesario)</span>
                <textarea rows={3} value={vozTexto} onChange={(e) => setVozTexto(e.target.value)} disabled={vozCargando} />
              </label>
            )}

            {vozMensaje && !vozCargando && (
              <div className="alert-info" role="status">{vozMensaje}</div>
            )}

            {vozError && <p className="alert-error" role="alert">{vozError}</p>}
            {vozCargando && <div className="empty" style={{ padding: 32 }}><span>{vozMensaje || 'Procesando…'}</span></div>}

            <div className="form-actions">
              <button type="button" className="btn" onClick={cerrarModalVoz}>Cancelar</button>
              {vozTexto ? (
                <button type="button" className="btn btn-primary" onClick={regenerarLineasVoz} disabled={vozCargando}>
                  <FiCpu /> {vozCargando ? 'Generando…' : 'Generar líneas'}
                </button>
              ) : (
                <button type="button" className="btn btn-primary" onClick={ejecutarVoz} disabled={!vozArchivo || vozCargando || vozGrabando}>
                  <FiCpu /> {vozCargando ? 'Procesando…' : 'Transcribir y generar líneas'}
                </button>
              )}
            </div>
          </div>
        ) : vozLineasEditadas.length > 0 ? (() => {
          const lineasEmparejadas = vozLineasEditadas.filter((l) => l.emparejado).length;
          const lineasSinEmparejar = vozLineasEditadas.length - lineasEmparejadas;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="alert-info">
                Se generaron {vozLineasEditadas.length} líneas a partir de la nota de voz.
                <strong> {lineasEmparejadas} emparejadas</strong> con tu catálogo,
                <strong style={{ color: '#f59e0b' }}> {lineasSinEmparejar} requieren revisión</strong>.
              </div>

              <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>
                Paso 2: comprueba la línea y corrige solo los datos que estén mal. Después pulsa “Añadir esta línea”.
              </div>

              {vozAvisos.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {vozAvisos.map((a, i) => (
                    <li key={i} style={{ fontSize: 13, color: a.severidad === 'alta' ? '#dc2626' : a.severidad === 'media' ? '#d97706' : 'var(--text-soft)' }}>
                      {a.mensaje}
                    </li>
                  ))}
                </ul>
              )}

              <div style={{ display: 'grid', gap: 12 }}>
                {vozLineasEditadas.map((l, i) => (
                  <div key={i} style={{ border: '1px solid var(--border, #dbe3ef)', borderRadius: 12, padding: 14, background: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <strong>Línea {i + 1}</strong>
                      {l.emparejado
                        ? <span className="badge badge-ok" style={{ fontSize: 11 }}>✓ {l.nombreCatalogo} ({l.confianza}%)</span>
                        : <span className="badge badge-warning" style={{ fontSize: 11 }}>⚠ Revisión</span>}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
                      <label className="field" style={{ margin: 0 }}>
                        <span>Descripción</span>
                        <InputDescripcionConOrtografia value={l.descripcion} onChange={(val) => updLineaVoz(i, 'descripcion', val)} />
                      </label>
                      <label className="field" style={{ margin: 0 }}>
                        <span>Cantidad</span>
                        <input type="number" min="0" value={l.cantidad} onChange={(e) => updLineaVoz(i, 'cantidad', e.target.value)} />
                      </label>
                      <label className="field" style={{ margin: 0 }}>
                        <span>Precio unitario</span>
                        <input type="number" step="0.01" value={l.precioUnitario} onChange={(e) => updLineaVoz(i, 'precioUnitario', e.target.value)} />
                      </label>
                      <label className="field" style={{ margin: 0 }}>
                        <span>IVA %</span>
                        <input type="number" value={l.iva} onChange={(e) => updLineaVoz(i, 'iva', e.target.value)} />
                      </label>
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-primary" onClick={usarLineasVoz}>Confirmar línea</button>
              </div>
            </div>
          );
        })() : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="alert-info">
              <strong>Líneas añadidas:</strong> {vozLineasAcumuladas.length}
            </div>

            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Descripción</th><th>Cant.</th><th>Precio</th><th>IVA%</th></tr></thead>
                <tbody>
                  {vozLineasAcumuladas.map((l, i) => (
                    <tr key={`${l.descripcion}-${i}`}>
                      <td>{l.descripcion}</td>
                      <td>{l.cantidad}</td>
                      <td>{Number(l.precioUnitario || 0).toFixed(2)} €</td>
                      <td>{l.iva}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-primary" onClick={cerrarModalVoz}>Cerrar</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal abierto={!!facturaObservaciones} onCerrar={() => { if (!guardandoObservaciones) { setFacturaObservaciones(null); onCerrarAccion?.(); } }} titulo="Observaciones de la factura" ancho={520}>
        <form onSubmit={guardarObservacionesFactura} className="form-grid">
          {facturaObservaciones && <div className="span-2" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn" onClick={() => abrirPDF(facturaObservaciones._id)}><FiDownload /> Descargar PDF</button>
            {accionesDocumento(usuario, empresa, 'facturas', facturaObservaciones).enviar && <button type="button" className="btn" onClick={() => prepararEnvioFirma(facturaObservaciones, 'facturas')}><FiSend /> Enviar factura al cliente</button>}
            {accionesDocumento(usuario, empresa, 'facturas', facturaObservaciones).verifactu && <button type="button" className="btn" onClick={() => confirmarAccion('¿Enviar esta factura a Verifactu?', () => accion(`/facturas/${facturaObservaciones._id}/enviar-verifactu`, 'Factura enviada a Verifactu'))}><FiSend /> Enviar a Verifactu</button>}
            {accionesDocumento(usuario, empresa, 'facturas', facturaObservaciones).pagar && <button type="button" className="btn" onClick={() => marcarPagada(facturaObservaciones._id)}><FiCheckCircle /> Marcar pagada</button>}
            {accionesDocumento(usuario, empresa, 'facturas', facturaObservaciones).eliminar && <button type="button" className="btn danger" title="Eliminar" onClick={() => eliminar('facturas', facturaObservaciones._id)}><FiTrash2 /> Eliminar</button>}
          </div>}
          <label className="field span-2">
            <span>Observaciones de la factura</span>
            <textarea rows={4} value={observacionesFactura} onChange={(event) => setObservacionesFactura(event.target.value)} disabled={guardandoObservaciones} />
          </label>
          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => { setFacturaObservaciones(null); onCerrarAccion?.(); }} disabled={guardandoObservaciones}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardandoObservaciones}>{guardandoObservaciones ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </form>
      </Modal>

      <Modal abierto={modal} onCerrar={cerrarFormulario} titulo={`${editando ? 'Editar' : 'Nuevo'} ${tipoDoc === 'facturas' ? 'factura' : tipoDoc.slice(0, -1)}`} ancho={820}>
        <form onSubmit={onGuardar} onKeyDown={manejarTeclaFormulario} className="form-grid">
          <div className="alert-info span-2" style={{ marginBottom: 8 }}>
            Sigue estos pasos para crear el documento sin complicarte.
          </div>

          <div className="span-2" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {['Cliente', 'Líneas', 'Resumen', 'Observaciones'].map((label, index) => (
              <button
                key={label}
                type="button"
                className={`btn btn-sm ${pasoActual === index ? 'btn-primary' : ''}`}
                onClick={() => {
                  if (index === 0 || index <= pasoActual || (index === 1 && cabecera.clienteId)) {
                    setPasoActual(index);
                  } else if (index === 1 && !cabecera.clienteId) {
                    mostrarNotificacion('warning', 'Primero elige un cliente para continuar.');
                  }
                }}
                style={{ opacity: index <= pasoActual ? 1 : 0.7, minWidth: 120 }}
              >
                {index + 1}. {label}
              </button>
            ))}
            {tipoDoc === 'presupuestos' && puedeAccederSimuladorBeneficio && (
              <button type="button" className="btn btn-sm" onClick={() => setSimuladorAbierto((v) => !v)}>
                {simuladorAbierto ? 'Ocultar simulador' : 'Simulador de beneficio'}
              </button>
            )}
          </div>

          {editando && (
            <div className="span-2" style={{ background: 'var(--bg-card, #f8fafc)', border: '1px solid var(--border, #e2e8f0)', padding: 14, borderRadius: 10, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <strong style={{ fontSize: '0.95rem' }}>Acciones del Documento</strong>
                  <div className="muted" style={{ fontSize: '0.82rem' }}>
                    {tipoDoc === 'pedidos' ? `Pedido ${numeroVisible(editando)} · ${editando.estado}` : editando.firmaInfo?.firmado
                      ? `Firmado digitalmente por ${editando.firmaInfo.firmadoPorNombre} (${editando.firmaInfo.firmadoPorNif}) el ${new Date(editando.firmaInfo.firmadoAt).toLocaleString('es-ES')}`
                      : `Envía ${tipoDoc === 'facturas' ? 'la factura' : 'el presupuesto'} al cliente con un enlace exclusivo de un solo uso para firma digital legal.`}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, width: '100%' }}>
                  {tipoDoc === 'presupuestos' && puedeVer && puedeCrear && (
                    <button
                      type="button"
                      className="btn"
                      title="Clonar presupuesto"
                      aria-label="Clonar presupuesto"
                      onClick={() => clonarPresupuesto(editando)}
                      disabled={clonando || guardando}
                      style={{ whiteSpace: 'normal', textAlign: 'left' }}
                    >
                      <FiCopy /> {clonando ? 'Clonando...' : 'Clonar presupuesto'}
                    </button>
                  )}
                  {tipoDoc === 'presupuestos' && puedeAccederSimuladorBeneficio && (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setSimuladorAbierto((v) => !v)}
                      style={{ whiteSpace: 'normal', textAlign: 'left' }}
                    >
                      <FiCpu /> {simuladorAbierto ? 'Ocultar simulador de beneficio' : 'Abrir simulador de beneficio'}
                    </button>
                  )}
                  {tipoDoc !== 'pedidos' && accionesDocumento(usuario, empresa, tipoDoc, editando).enviar && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => prepararEnvioFirma(editando)}
                      disabled={generandoFirma}
                      style={{ whiteSpace: 'normal', textAlign: 'left' }}
                    >
                      <FiSend /> {generandoFirma ? 'Generando enlace…' : `Enviar ${tipoDoc === 'facturas' ? 'factura' : 'presupuesto'} al cliente`}
                    </button>
                  )}
                  {tipoDoc === 'presupuestos' && puedeEditar && (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => convertirPresupuesto(editando)}
                      disabled={convirtiendo || editando.estado === 'convertido'}
                      style={{ whiteSpace: 'normal', textAlign: 'left' }}
                    >
                      <FiArrowRight /> {editando.estado === 'convertido' ? 'Pedido ya convertido' : 'Convertir en pedido'}
                    </button>
                  )}
                  {tipoDoc === 'pedidos' && (
                    <PedidoAcciones pedido={editando} onEditar={(doc) => abrirEditar('pedidos', doc)} onActualizado={async () => { await cargar(); if (onPedidoGuardado) await onPedidoGuardado(); }} onEliminado={cerrarFormulario} />
                  )}
                  {tipoDoc !== 'pedidos' && accionesDocumento(usuario, empresa, tipoDoc, editando).eliminar && (
                    <button type="button" className="btn danger" title="Eliminar" onClick={() => eliminar(tipoDoc, editando._id)} disabled={guardando}><FiTrash2 /> Eliminar</button>
                  )}
                  {(tipoDoc === 'presupuestos' || tipoDoc === 'facturas') && (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => abrirPDF(editando._id, tipoDoc)}
                      style={{ whiteSpace: 'normal', textAlign: 'left' }}
                    >
                      <FiDownload /> Descargar PDF
                    </button>
                  )}
                  {tipoDoc === 'facturas' && puedeEditar && !editando.verifactu?.enviado && (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => confirmarAccion('¿Deseas enviar esta factura a Verifactu y continuar?', () => accion(`/facturas/${editando._id}/enviar-verifactu`, 'Factura enviada a Verifactu'))}
                      style={{ whiteSpace: 'normal', textAlign: 'left' }}
                    >
                      <FiSend /> Enviar a Verifactu
                    </button>
                  )}
                  {tipoDoc === 'facturas' && puedeEditar && !editando.verifactu?.enviado && editando.estado !== 'pagada' && (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => marcarPagada(editando._id)}
                      style={{ whiteSpace: 'normal', textAlign: 'left' }}
                    >
                      <FiCheckCircle /> Marcar pagada
                    </button>
                  )}
                </div>
              </div>

              {editando.firmaInfo?.firmado && (
                <div className="alert-ok" style={{ marginTop: 10, fontSize: '0.85rem' }}>
                  ✅ <strong>Firmado Digitalmente:</strong> el cliente firmó el {new Date(editando.firmaInfo.firmadoAt).toLocaleString('es-ES')} desde IP {editando.firmaInfo.ip || 'registrada'}.
                </div>
              )}

              {documentosFirmados.length > 0 && (
                <div style={{ marginTop: 16, borderTop: '1px solid var(--border, #e2e8f0)', paddingTop: 14 }}>
                  <strong style={{ display: 'block', marginBottom: 8 }}>Documentos firmados</strong>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {documentosFirmados.map((doc) => (
                      <details key={doc._id} open={doc.estado === 'firmada'} style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%)', border: '1px solid #dfeafc', borderRadius: 12, padding: '12px 14px', boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)' }}>
                        <summary style={{ cursor: 'pointer', listStyle: 'none', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '2px 2px 10px 2px', borderBottom: '1px solid rgba(148, 163, 184, 0.35)' }}>
                          <span>{doc.documento?.numero || 'Documento'}</span>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#334155', background: '#e2e8f0', borderRadius: 999, padding: '4px 8px' }}>
                            {doc.firmado ? 'Firmado digitalmente' : 'Envío pendiente'} · {doc.firmadoAt ? new Date(doc.firmadoAt).toLocaleString('es-ES') : 'Sin fecha'}
                          </span>
                        </summary>
                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <a href={doc.pdfUrl} target="_blank" rel="noreferrer" className="btn btn-sm">PDF enviado</a>
                            {doc.pdfFirmadoDisponible && <a href={doc.pdfFirmadoUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-primary">PDF firmado</a>}
                          </div>
                          {doc.estado === 'firmada' && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, fontSize: '0.8rem', background: '#ffffff', border: '1px solid #dbeafe', borderRadius: 10, padding: 12, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)' }}>
                              <div style={{ padding: '6px 8px', borderRadius: 8, background: '#ecfdf5', border: '1px solid #bbf7d0' }}><strong>Estado:</strong> {doc.estado}</div>
                              <div style={{ padding: '6px 8px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}><strong>Firmado por:</strong> {doc.firmadoPorNombre || '—'}</div>
                              <div style={{ padding: '6px 8px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}><strong>NIF:</strong> {doc.firmadoPorNif || '—'}</div>
                              <div style={{ padding: '6px 8px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}><strong>Fecha:</strong> {doc.firmadoAt ? new Date(doc.firmadoAt).toLocaleString('es-ES') : '—'}</div>
                              <div style={{ padding: '6px 8px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}><strong>IP:</strong> {doc.ip || '—'}</div>
                              <div style={{ padding: '6px 8px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0' }}><strong>Enlace:</strong> <a href={doc.publicUrl} target="_blank" rel="noreferrer">Abrir</a></div>
                              <div style={{ gridColumn: '1 / -1', padding: '8px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', wordBreak: 'break-word' }}><strong>Hash:</strong> {doc.hashDocumento || '—'}</div>
                              <div style={{ gridColumn: '1 / -1', padding: '8px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', wordBreak: 'break-word' }}><strong>Agente:</strong> {doc.userAgent || '—'}</div>
                              {doc.reemplazadaPorToken && (
                                <div style={{ gridColumn: '1 / -1', padding: '8px 10px', borderRadius: 8, background: '#fff7ed', border: '1px solid #fed7aa', wordBreak: 'break-word' }}><strong>Reemplazado por:</strong> {doc.reemplazadaPorToken || '—'}</div>
                              )}
                              {doc.motivo && (
                                <div style={{ gridColumn: '1 / -1', padding: '8px 10px', borderRadius: 8, background: '#fff7ed', border: '1px solid #fed7aa', wordBreak: 'break-word' }}><strong>Motivo:</strong> {doc.motivo}</div>
                              )}
                            </div>
                          )}
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <fieldset className="span-2" disabled={editando && ((tipoDoc === 'pedidos' && !accionesPedido(usuario, editando).editar) || (tipoDoc === 'presupuestos' && !presupuestoEditable(editando)))} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'contents' }}>
          {pasoActual === 0 && (
            <>
              <label className="field">
                <span>{tipoDoc === 'facturas' ? 'Número factura custom' : 'Número presupuesto custom'}</span>
                <input
                  value={cabecera.numeroCustom || ''}
                  onChange={(e) => setCabecera((c) => ({ ...c, numeroCustom: normalizarNumeroCustom(tipoDoc, e.target.value) }))}
                  placeholder={tipoDoc === 'facturas' ? 'FAC-2026-001' : 'PRE-2026-001'}
                />
              </label>
              {!String(cabecera.numeroCustom || '').trim() && (
                <div className="alert-warning span-2" style={{ marginTop: -6 }}>
                  Se usará el código autogenerado no consecutivo.
                </div>
              )}
              <label className="field span-2"><span>Cliente *</span>
                <select required value={cabecera.clienteId} onChange={(e) => setCabecera((c) => ({ ...c, clienteId: e.target.value }))}>
                  <option value="">Selecciona un cliente</option>
                  {clientes.map((c) => <option key={c._id} value={c._id}>{c.nombre}</option>)}
                </select>
              </label>
              {plantillas.length > 0 && (
                <label className="field span-2"><span>Cargar plantilla</span>
                  <select defaultValue="" onChange={(e) => { cargarPlantilla(e.target.value); e.target.value = ''; }}>
                    <option value="">— Selecciona una plantilla —</option>
                    {plantillas.map((p) => <option key={p._id} value={p._id}>{p.nombre}</option>)}
                  </select>
                </label>
              )}
            </>
          )}

          {pasoActual === 1 && (
            <>
              <LineasEditor lineas={lineas} setLineas={setLineas} productos={productos} onAddByVoice={abrirModalVozDesdeFormulario} />

              {puedeAccederSimuladorBeneficio && (
                <div className="span-2" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #fef3c7 100%)', border: '1px solid rgba(251, 191, 36, 0.45)', borderRadius: 14, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                    <div>
                      <strong style={{ display: 'block', fontSize: '0.98rem' }}>Horas de trabajadores y beneficio</strong>
                      <span style={{ color: 'var(--text-soft)', fontSize: '0.82rem' }}>Registra tiempo real por empleado y simula el margen de beneficio del presupuesto.</span>
                    </div>
                    <button type="button" className="btn btn-sm" onClick={() => setSimuladorAbierto((v) => !v)}>
                      {simuladorAbierto ? 'Ocultar simulador' : 'Abrir simulador'}
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 12 }}>
                    <label className="field" style={{ margin: 0 }}>
                      <span>Horas presupuestadas</span>
                      <input type="number" min="0" step="0.5" value={horasPresupuestadas} onChange={(e) => setHorasPresupuestadas(e.target.value)} />
                    </label>
                    <label className="field" style={{ margin: 0 }}>
                      <span>Coste hora</span>
                      <input type="number" min="0" step="0.01" value={costeHora} onChange={(e) => setCosteHora(e.target.value)} />
                    </label>
                    <label className="field" style={{ margin: 0 }}>
                      <span>Tarifa horaria</span>
                      <input type="number" min="0" step="0.01" value={tarifaHora} onChange={(e) => setTarifaHora(e.target.value)} />
                    </label>
                  </div>

                  <div style={{ display: 'grid', gap: 10 }}>
                    {horasTrabajadores.length === 0 ? (
                      <div className="empty" style={{ padding: 10 }}>Añade trabajadores para registrar horas y comparar margen.</div>
                    ) : (
                      horasTrabajadores.map((item, index) => (
                        <div key={`${item.usuarioId || item.nombre || index}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, alignItems: 'end', padding: '10px 12px', background: '#fff', border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 10 }}>
                          <label className="field" style={{ margin: 0 }}>
                            <span>Trabajador</span>
                            <select value={item.usuarioId} onChange={(e) => {
                              const selected = clientes.find((c) => String(c._id) === String(e.target.value));
                              setHorasTrabajadores((prev) => prev.map((h, idx) => idx === index ? { ...h, usuarioId: e.target.value, nombre: selected ? selected.nombre : h.nombre } : h));
                            }}>
                              <option value="">Sin trabajador</option>
                              {clientes.map((cliente) => <option key={cliente._id} value={cliente._id}>{cliente.nombre}</option>)}
                            </select>
                          </label>
                          <label className="field" style={{ margin: 0 }}>
                            <span>Nombre manual</span>
                            <input value={item.nombre} onChange={(e) => setHorasTrabajadores((prev) => prev.map((h, idx) => idx === index ? { ...h, nombre: e.target.value } : h))} placeholder="Ej: Pepe" />
                          </label>
                          <label className="field" style={{ margin: 0 }}>
                            <span>Horas</span>
                            <input type="number" min="0" step="0.5" value={item.horas} onChange={(e) => setHorasTrabajadores((prev) => prev.map((h, idx) => idx === index ? { ...h, horas: e.target.value } : h))} />
                          </label>
                          <button type="button" className="icon-btn danger" onClick={() => setHorasTrabajadores((prev) => prev.filter((_, idx) => idx !== index))} title="Eliminar trabajador"><FiTrash2 /></button>
                        </div>
                      ))
                    )}

                    <button type="button" className="btn btn-sm" onClick={() => setHorasTrabajadores((prev) => [...prev, { ...HORAS_TRABAJADOR_VACIA }])}>
                      <FiPlus /> Añadir trabajador
                    </button>
                  </div>

                  {simuladorAbierto && (
                    <div style={{ background: '#fff', border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 12, padding: 12 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                        <div className="stat-card">
                          <p className="stat-label">Horas totales</p>
                          <p className="stat-value">{simulacionBeneficio.horasTotalesTrabajadas.toFixed(1)} h</p>
                        </div>
                        <div className="stat-card">
                          <p className="stat-label">Coste mano obra</p>
                          <p className="stat-value">{Number(simulacionBeneficio.costeManoObra || 0).toFixed(2)} €</p>
                        </div>
                        <div className="stat-card">
                          <p className="stat-label">Ingresos estimados</p>
                          <p className="stat-value">{Number(simulacionBeneficio.ingresosEstimados || 0).toFixed(2)} €</p>
                        </div>
                        <div className="stat-card">
                          <p className="stat-label">Beneficio bruto</p>
                          <p className="stat-value" style={{ color: (simulacionBeneficio.beneficio || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                            {Number(simulacionBeneficio.beneficio || 0).toFixed(2)} €
                          </p>
                        </div>
                      </div>
                      <div style={{ marginTop: 12, fontWeight: 700, color: (simulacionBeneficio.margen || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                        Margen estimado: {Number(simulacionBeneficio.margen || 0).toFixed(1)}%
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tipoDoc === 'facturas' && !editando && (
                <label className="field checkbox span-2" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Enviar también esta factura a Verifactu ahora</span>
                  <input type="checkbox" checked={enviarAVerifactu} onChange={(e) => setEnviarAVerifactu(e.target.checked)} />
                </label>
              )}
            </>
          )}

          {pasoActual === 2 && (
            <div className="span-2" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)', border: '1px solid rgba(148, 163, 184, 0.4)', borderRadius: 14, padding: 16 }}>
              <strong style={{ display: 'block', marginBottom: 10 }}>Resumen final</strong>
              <div style={{ display: 'grid', gap: 10 }}>
                <div><strong>Cliente:</strong> {clientes.find((c) => String(c._id) === String(cabecera.clienteId))?.nombre || 'Sin cliente'}</div>
                <div><strong>Líneas:</strong> {lineas.length}</div>
                <div><strong>Importe total:</strong> {calcTotales(lineas).total.toFixed(2)} €</div>
              </div>
            </div>
          )}

          {pasoActual === 3 && (
            <div className="field span-2">
              <label><span>Observaciones</span></label>
              <textarea
                rows={4}
                value={cabecera.notas}
                onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) event.stopPropagation(); }}
                onChange={(e) => setCabecera((c) => ({ ...c, notas: e.target.value }))}
                placeholder="Añade observaciones para el documento final, por ejemplo: datos de pago, condiciones, avisos, etc."
              />
              <small className="muted" style={{ display: 'block', marginTop: 6 }}>
                Esta información se incluirá en el PDF del presupuesto o la factura para que aparezca junto al documento final.
              </small>
            </div>
          )}
          </fieldset>

          <div className="form-actions span-2" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn" onClick={cerrarFormulario}>Cancelar</button>
              {pasoActual > 0 && (
                <button type="button" className="btn" onClick={() => setPasoActual((p) => Math.max(p - 1, 0))}>Anterior</button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {pasoActual < 3 ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    avanzarPaso();
                  }}
                >
                  Siguiente
                </button>
              ) : (
                <button type="submit" className="btn btn-primary" disabled={guardando || (tipoDoc === 'pedidos' && editando && !accionesPedido(usuario, editando).editar) || (tipoDoc === 'presupuestos' && editando && !presupuestoEditable(editando))}>{guardando ? 'Guardando…' : 'Guardar'}</button>
              )}
            </div>
          </div>
        </form>
      </Modal>

      <Modal abierto={modalStockForzado} onCerrar={() => setModalStockForzado(false)} titulo="Stock insuficiente" ancho={520}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p className="alert-error" role="alert">No hay stock suficiente para generar esta factura:</p>
          <ul>
            {stockInsuficienteDetalle.map((d, i) => (
              <li key={i}>{d.producto || d.id} — disponible: {d.disponible ?? '—'}, solicitado: {d.solicitado ?? '—'}{d.motivo ? ` (${d.motivo})` : ''}</li>
            ))}
          </ul>
          <label className="field">
            <span>Comentario (obligatorio para continuar)</span>
            <textarea rows={3} value={comentarioStock} onChange={(e) => setComentarioStock(e.target.value)} required />
          </label>
          <div className="form-actions">
            <button type="button" className="btn" onClick={() => setModalStockForzado(false)}>Cancelar</button>
            <button type="button" className="btn btn-primary" onClick={confirmarStockForzado} disabled={guardando || !comentarioStock.trim()}>Continuar sin stock suficiente</button>
          </div>
        </div>
      </Modal>

      <Modal abierto={modalGuardado} onCerrar={() => { setModalGuardado(false); setDocumentoGuardado(null); }} titulo="Documento guardado" ancho={560}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="alert-ok" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '14px 16px', fontWeight: 700 }}>
            ✅ {tipoDoc === 'facturas' ? 'Factura' : tipoDoc === 'pedidos' ? 'Pedido' : 'Presupuesto'} guardado correctamente
          </div>

          <div style={{ textAlign: 'center', fontSize: '0.96rem', color: 'var(--text-soft)' }}>
            ¿Qué quieres hacer ahora?
          </div>

          <div style={{ display: 'grid', gap: 12 }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                if (documentoGuardado?._id) abrirPDF(documentoGuardado._id, tipoDoc);
                setModalGuardado(false);
                setDocumentoGuardado(null);
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', fontSize: '1rem', fontWeight: 700 }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><FiDownload size={20} /> Exportar a PDF</span>
              <span style={{ fontSize: '0.78rem', opacity: 0.9 }}>Descargar</span>
            </button>

            <button
              type="button"
              className="btn"
              onClick={() => {
                const clienteSeleccionado = clientes.find((c) => String(c._id) === String((documentoGuardado?.clienteId || cabecera.clienteId || '')));
                if (!clienteSeleccionado) {
                  mostrarNotificacion('warning', 'No se pudo identificar el cliente para enviar por WhatsApp.');
                  setModalGuardado(false);
                  setDocumentoGuardado(null);
                  return;
                }

                const docTipo = tipoDoc === 'facturas' ? 'factura' : tipoDoc === 'pedidos' ? 'pedido' : 'presupuesto';
                const textoBody = `Estimado/a ${clienteSeleccionado.nombre || 'cliente'},\n\n` +
                  `Adjunto ${docTipo} con referencia ${documentoGuardado?.numeroCustom || documentoGuardado?.numero || ''}.\n\n` +
                  `Puedes revisarlo y confirmar la operación desde el enlace que te remitiremos.\n\n` +
                  `Un saludo.`;

                setModalGuardado(false);
                setModalEmailDoc({
                  cliente: clienteSeleccionado,
                  asunto: `Envío de ${docTipo}`,
                  cuerpoTexto: textoBody,
                  tipo: docTipo,
                  documentoId: documentoGuardado?._id,
                  documentoTipo: docTipo === 'factura' ? 'factura' : 'presupuesto',
                  numeroDocumento: documentoGuardado?.numeroCustom || documentoGuardado?.numero || '',
                  numeroCustom: documentoGuardado?.numeroCustom || '',
                });
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', fontSize: '1rem', fontWeight: 700 }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><FiSend size={20} /> Enviar por WhatsApp</span>
              <span style={{ fontSize: '0.78rem', opacity: 0.9 }}>Mensaje</span>
            </button>

            <button
              type="button"
              className="btn"
              onClick={() => {
                const clienteSeleccionado = clientes.find((c) => String(c._id) === String((documentoGuardado?.clienteId || cabecera.clienteId || '')));
                if (!clienteSeleccionado) {
                  mostrarNotificacion('warning', 'No se pudo identificar el cliente para enviar por email.');
                  setModalGuardado(false);
                  setDocumentoGuardado(null);
                  return;
                }

                const docTipo = tipoDoc === 'facturas' ? 'factura' : tipoDoc === 'pedidos' ? 'pedido' : 'presupuesto';
                const textoBody = `Estimado/a ${clienteSeleccionado.nombre || 'cliente'},\n\n` +
                  `Adjunto ${docTipo} con referencia ${documentoGuardado?.numeroCustom || documentoGuardado?.numero || ''}.\n\n` +
                  `Un saludo.`;

                setModalGuardado(false);
                setModalEmailDoc({
                  cliente: clienteSeleccionado,
                  asunto: `Documento ${docTipo}`,
                  cuerpoTexto: textoBody,
                  tipo: docTipo,
                  documentoId: documentoGuardado?._id,
                  documentoTipo: docTipo === 'factura' ? 'factura' : 'presupuesto',
                  numeroDocumento: documentoGuardado?.numeroCustom || documentoGuardado?.numero || '',
                  numeroCustom: documentoGuardado?.numeroCustom || '',
                });
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', fontSize: '1rem', fontWeight: 700 }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><FiSend size={20} /> Enviar por email</span>
              <span style={{ fontSize: '0.78rem', opacity: 0.9 }}>Correo</span>
            </button>

            <button
              type="button"
              className="btn"
              onClick={() => {
                setModalGuardado(false);
                setDocumentoGuardado(null);
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px 16px', fontSize: '1rem', fontWeight: 700 }}
            >
              Salir
            </button>
          </div>
        </div>
      </Modal>

      <ModalEnviarEmailCliente
        abierto={!!modalEmailDoc}
        onCerrar={() => setModalEmailDoc(null)}
        cliente={modalEmailDoc?.cliente}
        asuntoInicial={modalEmailDoc?.asunto}
        cuerpoTextoInicial={modalEmailDoc?.cuerpoTexto}
        tipoInicial={modalEmailDoc?.tipo}
        documentoId={modalEmailDoc?.documentoId}
        documentoTipo={modalEmailDoc?.documentoTipo}
        numeroDocumento={modalEmailDoc?.numeroDocumento}
        numeroCustom={modalEmailDoc?.numeroCustom}
        onEnviado={() => {
          mostrarNotificacion('success', 'Enlace de firma digital enviado al cliente por correo.');
          cargar();
        }}
      />
    </div>
  );
}
